// Dash gifts, from /api/v1/admin/gifts (Hethera docs/gift-acceptance-criteria.md G11).
// Money fields are Decimal strings; Number() them before formatting.

export type GiftStatus =
  | 'DRAFT'
  | 'PAYING'
  | 'PAYMENT_FAILED'
  | 'PAYMENT_REVERSED'
  | 'SCHEDULED'
  | 'READY'
  | 'DELIVERING'
  | 'DELIVERED'
  | 'CANCELLED'
  | 'UNDELIVERABLE'
  | 'UNCLAIMED'
  | 'DECLINED'
  | 'DELIVERY_FAILED'
  | 'FROZEN'
  | 'REVOKED';

export const GIFT_STATUS_OPTIONS: GiftStatus[] = [
  'SCHEDULED',
  'READY',
  'DELIVERING',
  'DELIVERED',
  'FROZEN',
  'DELIVERY_FAILED',
  'DECLINED',
  'UNCLAIMED',
  'UNDELIVERABLE',
  'CANCELLED',
  'REVOKED',
  'PAYING',
  'PAYMENT_FAILED',
  'PAYMENT_REVERSED',
  'DRAFT',
];

export type GiftRefundStatus = 'NONE' | 'PENDING' | 'SUCCEEDED' | 'FAILED';

export interface GiftRow {
  id: string;
  code: string;
  sender_id: string;
  recipient_id: string | null;
  sender_chat_id: string | null;
  recipient_chat_id: string | null;
  bill_type: 'AIRTIME' | 'ELECTRICITY' | null;
  amount: string | null;
  fee: string;
  occasion: string | null;
  status: GiftStatus;
  deliver_at: string | null;
  ready_at: string | null;
  delivered_at: string | null;
  refund_status: GiftRefundStatus;
  refund_reason: string | null;
  created_at: string;
}

export interface GiftAudit {
  event_type: string;
  previous_status: string | null;
  new_status: string | null;
  audit_metadata: Record<string, unknown> | null;
  actor: string | null;
  created_at: string;
}

export interface GiftTransaction {
  reference: string;
  type: string;
  funding_source: string;
  status: string;
  amount: string;
  created_at: string | null;
}

export interface GiftDetail extends GiftRow {
  service_id: string | null;
  billers_code: string | null;
  meter_type: string | null;
  customer_name: string | null;
  funding_transaction_id: string | null;
  delivery_transaction_id: string | null;
  delivery_attempts: number;
  recipient_attempts: number;
  refund_amount: string | null;
  refunded_at: string | null;
  audits: GiftAudit[];
  transactions: GiftTransaction[];
}

// What each admin action does and when the backend allows it (G11.3).
export type GiftAction = 'freeze' | 'unfreeze' | 'revoke' | 'retry-refund' | 'deliver-now';

export const GIFT_ACTIONS: Record<
  GiftAction,
  { label: string; needsNote: boolean; allowed: (gift: GiftRow) => boolean; confirm?: string }
> = {
  freeze: {
    label: 'Freeze',
    needsNote: true,
    allowed: (g) => g.status === 'SCHEDULED' || g.status === 'READY',
  },
  unfreeze: { label: 'Unfreeze', needsNote: true, allowed: (g) => g.status === 'FROZEN' },
  revoke: {
    label: 'Revoke and refund sender',
    needsNote: true,
    allowed: (g) => g.status === 'FROZEN',
    confirm: 'Refund the sender in full and end this gift? This cannot be undone.',
  },
  'retry-refund': {
    label: 'Retry refund',
    needsNote: false,
    allowed: (g) => g.refund_status === 'FAILED',
  },
  'deliver-now': {
    label: 'Re-attempt delivery',
    needsNote: false,
    allowed: (g) => g.status === 'DELIVERING',
    confirm: 'Start one more delivery attempt? Only do this once the provider problem is fixed.',
  },
};
